// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app:
//   https://api.orioralabs.com/api/apps/study_companion/{digest|explain|plan}
// with the customer's Oriora session token. The server holds the prompts + bills the
// Oriora wallet (Managed AI / BYOK). The study library stays local; only the text the
// user explicitly submits for THAT action leaves the device.
//
// ONE billed AI call per user action (three actions, three endpoints — never chained,
// never looped). CRUD, not agentic: scoped JSON in, strict JSON out. No agent loop,
// no 2nd call, no tool-use chain, no direct OpenAI/Anthropic SDK here.
//
// HARDEST guardrails:
//   • STUDY-TOOL fence (Creem essay-mill ban): this app EXPLAINS and TEACHES. It NEVER
//     writes essays, assignments, homework answers, or submission-ready text. A hard
//     PRE-LLM refusal (no billed call) catches do-my-work asks — see academicRefusal()
//     below + the matching server-side prompt rules on all three endpoints.
//   • INJECTION fence: submitted material is UNTRUSTED DATA — the server wraps it in a
//     <data> boundary on top of the shared INJECTION_FENCE (BRAIN-PATTERNS Pattern 4).

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { Explanation, Level, StudyNotes, StudyPlan, PlanWeek } from "./store";

const SERVER = "https://api.orioralabs.com";
const APP_SLUG = "study_companion";

export type BrainErrorCode =
  | "insufficient_funds"
  | "managed_ai_blocked"
  | "unauthenticated"
  | "unavailable"
  | "refused_academic"
  | "no_store"
  | "too_large";

export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/${APP_SLUG}/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Add funds to keep using AI.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Heartbeat endpoints (/digest, /plan) lock the HTTP status to 200 once keepalive bytes
    // flush and encode failures IN the body via `_status` — same contract as workout_tracker
    // /draft_program. Leading whitespace IS the heartbeat: trim, parse, then map failures back
    // to the same BrainError classes the status switch above produces, so a failed call can
    // never read as "your material/deadline was the problem".
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    const text = (await res.text()).trim();
    let parsed: Record<string, unknown>;
    try { parsed = JSON.parse(text) as Record<string, unknown>; } catch { throw new BrainError("unavailable", "AI is busy — please try again."); }
    const bodyStatus = typeof parsed._status === "number" ? parsed._status : undefined;
    if (bodyStatus === 402) throw new BrainError("insufficient_funds", String(parsed.message ?? "Add funds to keep using AI."));
    if (bodyStatus === 403) throw new BrainError("managed_ai_blocked", String(parsed.message ?? "Connect a vendor key in Oriora settings to use AI for this app."));
    if (bodyStatus !== undefined) throw new BrainError("unavailable", String(parsed.message ?? parsed.error ?? "AI request failed."));
    return parsed as unknown as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

// ── STUDY-TOOL fence (hard, PRE-LLM) ────────────────────────────────────────────
// Oriora Study teaches the concepts in the user's own materials; it is NOT a
// do-my-homework service (Creem prohibits essay mills outright, and it's the wrong
// product anyway). Any request that reaches for a submission-ready deliverable is
// refused BEFORE any billed call. (Mirrored server-side in all three prompts.)
const ACADEMIC_PATTERNS: RegExp[] = [
  /\bwrite\s+(?:my|me|the|an?|this)\s+(?:essay|paper|report|assignment|thesis|dissertation|homework|coursework|lab report|book report|article|speech)\b/i,
  /\b(?:do|finish|complete|solve)\s+(?:my|the|this)\s+(?:homework|assignment|coursework|problem set|worksheet|take-?home|exam|quiz|test)\b/i,
  /\bessay\s+(?:about|on|for)\b/i,
  /\b(?:answer|answers)\s+(?:key|sheet)\b/i,
  /\bwrite\s+(?:it|this|that)\s+for\s+me\b/i,
  /\b\d{2,4}[- ]?word\s+(?:essay|paper|summary\s+to\s+(?:submit|hand))/i,
  /\bso\s+i\s+can\s+(?:submit|hand\s+(?:it\s+)?in|turn\s+(?:it\s+)?in)\b/i,
  /\bplagiar/i,
  /\bwithout\s+(?:it\s+)?(?:being|getting)\s+detected\b/i,
];

const ACADEMIC_REFUSAL =
  "I explain and teach — I don't write essays, assignments, or homework answers. Ask me to explain the concept instead.";

/** Returns the refusal message if the request trips the study-tool fence, else null.
 *  The caller blocks the AI call when this returns a string. */
// ── WHERE THE LINE IS: AUTHORSHIP ───────────────────────────────────────────────
// Creem's prohibited-products list bans "homework/essay mills". The QAA defines an essay mill as
// a service that CONTRACTS TO COMPLETE a student's assignment. The academic-integrity literature
// is consistent that the violation is about **authorship**, not about the tool: brainstorming,
// outlining, grammar support and feedback are ethical; submitting generated text as your own is
// misconduct — "whether the someone else is a human tutor-for-hire or a generative model doesn't
// matter." (QAA, "Contracting to Cheat in Higher Education"; Turnitin, contract-cheating guide.)
//
//   ALLOWED  — the STUDENT is the author: explain, teach, plan, quiz, outline, and give feedback
//              on words THEY wrote (clarity, grammar, structure, what's weak, is my argument clear).
//   REFUSED  — WE become the author: writing, rewriting, drafting, composing, producing, polishing
//              or finishing their deliverable; answers to graded assessments; evading detection.
//
// This is why the verb matters more than the noun. `\bwrite\b` never matched inside "rewrite",
// so "rewrite my essay" walked straight through the old fence.
const DELIVERABLE = "essay|paper|report|assignment|thesis|dissertation|homework|coursework|lab report|book report|article|speech|introduction|conclusion|paragraph";
const AUTHORSHIP_PATTERNS: RegExp[] = [
  // We do the writing: any authoring verb + their deliverable.
  new RegExp(`\\b(?:re-?write|rewrite|draft|compose|produce|generate|create|write\\s+up|finish|polish|expand|flesh\\s+out)\\s+(?:my|me|the|an?|this)\\s+(?:${DELIVERABLE})\\b`, "i"),
  // "make my essay better / stronger / longer" = we rewrite it.
  new RegExp(`\\bmake\\s+(?:my|the|this)\\s+(?:${DELIVERABLE})\\s+(?:better|stronger|longer|flow)\\b`, "i"),
  // Submission-ready output, however it's phrased.
  /\b(?:final|finished|submission[-\s]?ready)\s+(?:version|draft|copy|text)\b/i,
  /\bi\s+can\s+(?:hand\s+(?:it\s+)?in|submit|turn\s+(?:it\s+)?in)\b/i,
  /\bready\s+to\s+(?:submit|hand\s+in|turn\s+in)\b/i,
];

/**
 * Asking to be TAUGHT how to write something is the app's whole job — "explain how to write a
 * thesis statement" was refused by the `write a thesis` pattern. Teaching is exempt, UNLESS the
 * message also asks for the deliverable itself. A fence that refuses to teach isn't safety, it's
 * the app fighting the student.
 */
const TEACHING_REQUEST = /\b(?:how\s+(?:to|do\s+i|should\s+i)\s+write|explain\s+how|what\s+makes\s+a\s+good)\b/i;
const DO_IT_FOR_ME = /\b(?:for\s+me|my\s+(?:essay|paper|report|assignment|homework|coursework|thesis|dissertation)\b|so\s+i\s+can\s+(?:submit|hand|turn))/i;

export function academicRefusal(text: string): string | null {
  const t = text.trim();
  if (!t) return null;
  // Authorship asks are refused even when dressed up as a teaching question.
  if (AUTHORSHIP_PATTERNS.some((re) => re.test(t))) return ACADEMIC_REFUSAL;
  if (TEACHING_REQUEST.test(t) && !DO_IT_FOR_ME.test(t)) return null;
  return ACADEMIC_PATTERNS.some((re) => re.test(t)) ? ACADEMIC_REFUSAL : null;
}

// ── The three billed actions ────────────────────────────────────────────────────

/**
 * DIGEST — material text → structured study notes (key concepts, summary, self-check
 * questions with answers-as-explanations). ONE billed call. The material may be pasted
 * text or the on-device OCR of a photographed page — the photo never leaves the device.
 * Endpoint: POST /api/apps/study_companion/digest  (Bearer JWT, bills the wallet).
 */
export async function digestMaterial(material: string, opts?: { title?: string; topic?: string }): Promise<StudyNotes & { refusal: string }> {
  const text = material.trim();
  const refusal = academicRefusal(text.slice(0, 2000));
  if (refusal) throw new BrainError("refused_academic", refusal);
  if (text.length < 40) throw new BrainError("unavailable", "That's too short to digest — paste at least a paragraph.");
  const r = await brainCall<Partial<StudyNotes> & { refusal?: string }>("digest", {
    material: text,
    title: opts?.title ?? "",
    topic: opts?.topic ?? "",
  });
  return {
    key_concepts: Array.isArray(r.key_concepts)
      ? r.key_concepts
          .map((c) => ({ term: String(c?.term ?? "").trim(), definition: String(c?.definition ?? "").trim() }))
          .filter((c) => c.term && c.definition)
      : [],
    summary: String(r.summary ?? "").trim(),
    self_check: Array.isArray(r.self_check)
      ? r.self_check
          .map((q) => ({ question: String(q?.question ?? "").trim(), explanation: String(q?.explanation ?? "").trim() }))
          .filter((q) => q.question && q.explanation)
      : [],
    refusal: String(r.refusal ?? "").trim(),
  };
}

/**
 * EXPLAIN — a concept + the user's current understanding level → a LAYERED explanation:
 * simple analogy → precise definition → worked example → common misconception. ONE billed call.
 * Endpoint: POST /api/apps/study_companion/explain  (Bearer JWT, bills the wallet).
 */
export async function explainConcept(concept: string, level: Level, subject?: string): Promise<Explanation & { refusal: string }> {
  const c = concept.trim();
  const refusal = academicRefusal(c);
  if (refusal) throw new BrainError("refused_academic", refusal);
  if (c.length < 2) throw new BrainError("unavailable", "Name the concept you want explained.");
  const r = await brainCall<Partial<Explanation> & { refusal?: string }>("explain", {
    concept: c,
    level,
    subject: (subject ?? "").trim(),
  });
  return {
    concept: String(r.concept ?? c).trim(),
    analogy: String(r.analogy ?? "").trim(),
    definition: String(r.definition ?? "").trim(),
    worked_example: String(r.worked_example ?? "").trim(),
    misconception: String(r.misconception ?? "").trim(),
    refusal: String(r.refusal ?? "").trim(),
  };
}

export interface PlanInput {
  goal: string;
  /** YYYY-MM-DD target date. */
  deadline: string;
  weeklyHours: number;
  topics: string[];
  /** Client-local YYYY-MM-DD so week counts align to the user's clock. */
  today: string;
}

/**
 * PLAN — goal + deadline + topics + weekly hours → a week-by-week learning plan with
 * milestones and review points. ONE billed call.
 * Endpoint: POST /api/apps/study_companion/plan  (Bearer JWT, bills the wallet).
 */
export async function buildPlan(input: PlanInput): Promise<StudyPlan & { refusal: string }> {
  const goal = input.goal.trim();
  const refusal = academicRefusal(goal);
  if (refusal) throw new BrainError("refused_academic", refusal);
  if (!goal) throw new BrainError("unavailable", "Describe what you're working toward.");
  if (input.topics.length === 0) throw new BrainError("unavailable", "Add at least one topic to plan around.");
  const r = await brainCall<Partial<StudyPlan> & { refusal?: string }>("plan", {
    goal,
    deadline: input.deadline,
    weekly_hours: input.weeklyHours,
    topics: input.topics.slice(0, 30),
    today: input.today,
  });
  const weeks: PlanWeek[] = Array.isArray(r.weeks)
    ? r.weeks
        .map((w, i) => ({
          week: Number.isFinite(Number(w?.week)) ? Number(w?.week) : i + 1,
          label: String(w?.label ?? "").trim(),
          focus: Array.isArray(w?.focus) ? w.focus.map((f) => String(f).trim()).filter(Boolean) : [],
          milestone: String(w?.milestone ?? "").trim(),
          review: String(w?.review ?? "").trim(),
        }))
        .filter((w) => w.label || w.focus.length > 0)
    : [];
  return {
    goal: String(r.goal ?? goal).trim(),
    weeks,
    note: String(r.note ?? "").trim(),
    refusal: String(r.refusal ?? "").trim(),
  };
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds the FAITHFUL backup locally (export_backup); these calls relay it to the
// user's OWN connected cloud store — their GitHub repo or Google Drive (connected once in the
// Oriora account/Connections hub; the server picks whichever). Oriora RETAINS nothing. Plain
// HTTP → works on iOS with no file picker. Endpoints: POST/GET /api/apps/study_companion/backup.
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/${APP_SLUG}/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN
 *  cloud store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora
 *  retains nothing (text relay only). Throws no_store when nothing is connected. */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS
// browser — the in-app https handback is robust on iOS where the custom-scheme deep-link is
// fragile (mirrors expense_audit_ai). Session returns via auth:deep-link (App.tsx + ipc.rs).
// The web callback must allowlist `study-companion` as a return target (server-side, operator task).
export const openWebLogin = () => invoke<void>("open_login_window");
